import { createRouter, createWebHistory } from 'vue-router'
import UserRegistration from '@/views/AdminView/UserRegistration.vue'
import UserManagement from '@/views/AdminView/UserManagement.vue'
import FinanceDashboard from '../views/FinanceView/FinanceDashboard.vue'
import YearlyTargets from '../views/FinanceView/YearlyTargets.vue'
import FinanceCostSubmission from '../views/FinanceView/CostSubmission.vue'
import SubsidiaryManagement from '@/views/AdminView/SubsidiaryManagement.vue'
import FinanceSubsidiaryManagement from '@/views/FinanceView/SubsidiaryManagement.vue'
import AdminManagementRegistration from '@/views/FinanceView/AdminManagementRegistration.vue'
import RawangKpiManagement from '@/views/FinanceView/RawangKpiManagement.vue'
import axios from 'axios'
import { jwtDecode } from 'jwt-decode'
import SubsidiaryView from '../views/ManagementView/SubsidiaryView.vue'
import RawangHospitalDashboard from '../views/ManagementView/RawangHospitalDashboard.vue'
import WarehouseLayout from '../views/ManagementView/WarehouseLayout.vue'
import WarehouseDashboard from '../views/ManagementView/WarehouseDashboard.vue'
import WarehouseInventory from '../views/ManagementView/WarehouseInventory.vue'
import WarehouseSales from '../views/ManagementView/WarehouseSales.vue'
import WarehouseOrders from '../views/ManagementView/WarehouseOrders.vue'
import WarehouseClinic from '../views/ManagementView/WarehouseClinic.vue'
import WarehouseDental from '../views/ManagementView/WarehouseDental.vue'
import WarehouseDataFlow from '../views/ManagementView/WarehouseDataFlow.vue'
import WarehouseSources from '../views/ManagementView/WarehouseSources.vue'
import RevenueManagement from '../views/AdminView/RevenueManagement.vue'
import RevenueReport from '../views/FinanceView/RevenueReport.vue'
import SCMSTracker from '../views/FinanceView/SCMSTracker.vue'
import ManagementSCMSTracker from '../views/ManagementView/ManagementSCMSTracker.vue'

// Create axios instance
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  timeout: import.meta.env.VITE_API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Add auth token to requests
api.interceptors.request.use(config => {
  const token = sessionStorage.getItem('token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Helper function to check if token is expired
const isTokenExpired = (token) => {
  try {
    const decoded = jwtDecode(token)
    const currentTime = Date.now() / 1000
    return decoded.exp < currentTime
  } catch (error) {
    return true
  }
}

const warehouseMeta = {
  requiresAuth: true,
  layout: 'default',
  allowedRoles: ['Procurement']
}

const routes = [
  {
    path: '/',
    name: 'SignIn',
    component: () => import('../views/SignIn.vue'),
    meta: { layout: 'auth' }
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: () => import('../views/ResetPassword.vue'),
    meta: { layout: 'auth' }
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: () => import('../views/ManagementView/Dashboard.vue'),
    meta: { 
      requiresAuth: true,
      layout: 'default',
      allowedRoles: ['Management', 'Finance'],
      subsidiaryType: 'non_hospital'
    }
  },
  {
    path: '/dashboard/hospital',
    name: 'HospitalDashboard',
    component: () => import('../views/ManagementView/Dashboard.vue'),
    meta: { 
      requiresAuth: true,
      layout: 'default',
      allowedRoles: ['Management', 'Finance'],
      subsidiaryType: 'hospital'
    }
  },
  {
    path: '/management/rawang-dashboard',
    name: 'RawangHospitalDashboard',
    component: RawangHospitalDashboard,
    meta: {
      requiresAuth: true,
      layout: 'default',
      allowedRoles: ['Management', 'Finance']
    }
  },
  {
    path: '/warehouse',
    component: WarehouseLayout,
    meta: warehouseMeta,
    children: [
      { path: '', name: 'WarehouseDashboard', component: WarehouseDashboard, meta: warehouseMeta },
      { path: 'inventory', name: 'WarehouseInventory', component: WarehouseInventory, meta: warehouseMeta },
      { path: 'clinic', name: 'WarehouseClinic', component: WarehouseClinic, meta: warehouseMeta },
      { path: 'dental', name: 'WarehouseDental', component: WarehouseDental, meta: warehouseMeta },
      { path: 'orders', name: 'WarehouseOrders', component: WarehouseOrders, meta: warehouseMeta },
      { path: 'sales', name: 'WarehouseSales', component: WarehouseSales, meta: warehouseMeta },
      { path: 'data-flow', name: 'WarehouseDataFlow', component: WarehouseDataFlow, meta: warehouseMeta },
      { path: 'sources', name: 'WarehouseSources', component: WarehouseSources, meta: warehouseMeta }
    ]
  },
  {
    path: '/analytics/yearly',
    name: 'YearlyAnalytics',
    component: () => import('../views/ManagementView/YearlyAnalytics.vue'),
    meta: {
      requiresAuth: true,
      layout: 'default',
      allowedRoles: ['Management', 'Finance']
    }
  },
  {
    path: '/overview',
    name: 'ManagementViewOverview',
    component: () => import('../views/ManagementView/Overview.vue'),
    meta: { 
      requiresAuth: true,
      layout: 'default',
      allowedRoles: ['Management', 'Finance']
    }
  },
  {
    path: '/management/subsidiary/:subsidiary',
    name: 'SubsidiaryView',
    component: SubsidiaryView,
    meta: {
      requiresAuth: true,
      layout: 'default',
      allowedRoles: ['Management', 'Admin']
    }
  },
  {
    path: '/:subsidiary',
    redirect: to => `/management/subsidiary/${to.params.subsidiary}`,
    meta: {
      requiresAuth: true,
      allowedRoles: ['Management', 'Admin']
    }
  },
  {
    path: '/pic-dashboard',
    name: 'PicDashboard',
    component: () => import('../views/PicView/Dashboard.vue'),
    meta: { 
      requiresAuth: true,
      layout: 'default',
      allowedRoles: ['Person-In-Charge']
    }
  },
  {
    path: '/pic-daily-report',
    name: 'PicDailyReport',
    component: () => import('../views/PicView/DailyReport.vue'),
    meta: { 
      requiresAuth: true,
      layout: 'default',
      allowedRoles: ['Person-In-Charge']
    }
  },
  {
    path: '/pic-records',
    name: 'PicRecords',
    component: () => import('../views/PicView/PicRecords.vue'),
    meta: { 
      requiresAuth: true,
      layout: 'default',
      allowedRoles: ['Person-In-Charge']
    }
  },
  {
    path: '/admin-dashboard',
    name: 'AdminDashboard',
    component: () => import('../views/AdminView/Dashboard.vue'),
    meta: { 
      requiresAuth: true,
      layout: 'default',
      allowedRoles: ['Admin']
    }
  },
  {
    path: '/user-registration',
    name: 'UserRegistration',
    component: UserRegistration,
    meta: { 
      requiresAuth: true,
      layout: 'default',
      allowedRoles: ['Admin']
    }
  },
  {
    path: '/finance/dashboard',
    name: 'FinanceDashboard',
    component: FinanceDashboard,
    meta: {
      requiresAuth: true,
      layout: 'default',
      allowedRoles: ['Finance']
    }
  },
  {
    path: '/finance/yearly-targets',
    name: 'YearlyTargets',
    component: YearlyTargets,
    meta: {
      requiresAuth: true,
      layout: 'default',
      allowedRoles: ['Finance']
    }
  },
  {
    path: '/finance/yearly-budget',
    name: 'YearlyBudget',
    component: () => import('../views/FinanceView/YearlyBudget.vue'),
    meta: {
      requiresAuth: true,
      layout: 'default',
      allowedRoles: ['Finance']
    }
  },
  {
    path: '/finance/cost-submission',
    name: 'FinanceCostSubmission',
    component: FinanceCostSubmission,
    meta: {
      requiresAuth: true,
      layout: 'default',
      allowedRoles: ['Finance']
    }
  },
  {
    path: '/admin/subsidiaries',
    name: 'SubsidiaryManagement',
    component: SubsidiaryManagement,
    meta: { 
      requiresAuth: true,
      layout: 'default',
      allowedRoles: ['Admin']
    }
  },
  {
    path: '/finance/subsidiaries',
    name: 'FinanceSubsidiaryManagement',
    component: FinanceSubsidiaryManagement,
    meta: {
      requiresAuth: true,
      layout: 'default',
      allowedRoles: ['Finance']
    }
  },
  {
    path: '/finance/rawang-kpi',
    name: 'RawangKpiManagement',
    component: RawangKpiManagement,
    meta: {
      requiresAuth: true,
      layout: 'default',
      allowedRoles: ['Finance']
    }
  },
  {
    path: '/finance/admin-management-registration',
    name: 'AdminManagementRegistration',
    component: AdminManagementRegistration,
    meta: {
      requiresAuth: true,
      layout: 'default',
      allowedRoles: ['Finance']
    }
  },
  {
    path: '/admin/users',
    name: 'UserManagement',
    component: UserManagement,
    meta: { 
      requiresAuth: true,
      layout: 'default',
      allowedRoles: ['Admin']
    }
  },
  {
    path: '/admin/daily-report',
    name: 'AdminDailyReport',
    component: () => import('../views/AdminView/DailyReport.vue'),
    meta: { 
      requiresAuth: true,
      layout: 'default',
      allowedRoles: ['Admin']
    }
  },
  {
    path: '/admin/performance',
    name: 'PerformanceManagement',
    component: () => import('../views/AdminView/PerformanceManagement.vue'),
    meta: { 
      requiresAuth: true,
      layout: 'default',
      allowedRoles: ['Admin']
    }
  },
  {
    path: '/admin/revenue',
    name: 'RevenueManagement',
    component: RevenueManagement,
    meta: { 
      requiresAuth: true,
      layout: 'default',
      allowedRoles: ['Admin']
    }
  },
  {
    path: '/finance/revenue-report',
    name: 'RevenueReport',
    component: RevenueReport,
    meta: { 
      requiresAuth: true,
      layout: 'default',
      allowedRoles: ['Finance']
    }
  },
  {
    path: '/finance/scms-tracker',
    name: 'SCMSTracker',
    component: SCMSTracker,
    meta: { 
      requiresAuth: true,
      layout: 'default',
      allowedRoles: ['Finance']
    }
  },
  {
    path: '/management/scms-tracker',
    name: 'ManagementSCMSTracker',
    component: ManagementSCMSTracker,
    meta: { 
      requiresAuth: true,
      layout: 'default',
      allowedRoles: ['Management']
    }
  },
  {
    path: '/finance/revenue-adjustment',
    name: 'RevenueAdjustment',
    component: () => import('../views/FinanceView/RevenueAdjustment.vue'),
    meta: { 
      requiresAuth: true,
      layout: 'default',
      allowedRoles: ['Finance']
    }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// Navigation guard to check authentication and update body class
router.beforeEach((to, from, next) => {
  const token = sessionStorage.getItem('token')
  const userData = sessionStorage.getItem('user')
  const user = userData ? JSON.parse(userData) : null
  
  // Check if token is expired
  if (token && isTokenExpired(token)) {
    sessionStorage.removeItem('token')
    sessionStorage.removeItem('user')
    next('/')
    return
  }
  
  // Update body class based on layout
  document.body.classList.remove('auth-layout', 'default-layout')
  document.body.classList.add(to.meta.layout === 'auth' ? 'auth-layout' : 'default-layout')
  
  // Prevent authenticated users from accessing sign-in page
  if (to.path === '/' && token && user) {
    switch (user.role) {
      case 'Admin':
        next('/admin-dashboard')
        break
      case 'Person-In-Charge':
        next('/pic-dashboard')
        break
      case 'Management':
        next('/analytics/yearly')
        break
      case 'Finance':
        next('/finance/dashboard')
        break
      case 'Procurement':
        next('/warehouse')
        break
      default:
        next('/dashboard')
    }
    return
  }

  if (to.meta.requiresAuth) {
    if (!token || !user) {
      sessionStorage.removeItem('token')
      sessionStorage.removeItem('user')
      next('/')
      return
    }

    // Check for role-based access
    if (to.meta.allowedRoles) {
      const allowedRolesNormalized = to.meta.allowedRoles.map(role => String(role).toLowerCase())
      const hasAccess = allowedRolesNormalized.includes(String(user.role || '').toLowerCase())
      
      if (!hasAccess) {
        switch (user.role) {
          case 'Admin':
            next('/admin-dashboard')
            break
          case 'Person-In-Charge':
            next('/pic-dashboard')
            break
          case 'Management':
            next('/analytics/yearly')
            break
          case 'Finance':
            next('/finance/dashboard')
            break
          case 'Procurement':
            next('/warehouse')
            break
          default:
            next('/')
        }
        return
      }
    }

    next()
  } else {
    next()
  }
})

export default router 